import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MOVEMODE, STATE } from './camera-controls-constants';
import { CameraMoveType } from './camera-controls.interface';
import { OrthographicCamera, Vector2, Vector3 } from 'three';
import { MathUtils } from '../../util/math-utils';

export class CameraControlEventHandler {
  private _state: STATE = STATE.NONE;
  private _wirecontourVisibilityTimeout?: ReturnType<typeof setTimeout>;
  private _moveMode = MOVEMODE.normal;
  private _lastTouchEvent: TouchEvent | undefined;
  private _hideSceneObjects = false;

  private _contextMenuHandler = (event: MouseEvent) => (event.preventDefault());
  private _mouseWheelHandler = (event: WheelEvent) => this.onMouseWheel(event);
  private _mouseDownHandler = (event: MouseEvent) => this.onMouseDown(event);
  private _touchStartHandler = (event: TouchEvent) => this.onTouchStart(event);
  private _touchEndHandler = (event: TouchEvent) => this.onTouchEnd(event);
  private _touchMoveHandler = (event: TouchEvent) => this.onTouchMove(event);
  private _mouseMoveHandler = (event: MouseEvent) => this.onMouseMove(event);
  private _mouseUpHandler = (event: MouseEvent) => this.onMouseUp(event);
  private _keyDownHandler = (event: KeyboardEvent) => this.onKeyPress(event);

  public hiddenSceneObjects?: ArticleContainer[];
  public enabled = true;

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: OrthographicCamera,
    private renderScene: () => void,
    private updateWirecontourVisibility: () => void,
    private update: () => void,
    private rotateCamera: (panAngleDeg: number, tiltAngleDeg: number) => void,
    private zoomCamera: (value: number) => void,
    private moveCamera: (x: number, y: number) => void,
  ) {
    this.listenToEvents();
  }

  public set hideSceneObjects(hide: boolean) {
    this._hideSceneObjects = hide;
    this.updateWirecontourVisibility();
  }

  public get hideSceneObjects(): boolean {
    return this._hideSceneObjects;
  }

  private listenToEvents(): void {
    this.canvas.addEventListener('contextmenu', this._contextMenuHandler, false);
    this.canvas.addEventListener('mousedown', this._mouseDownHandler, false);
    this.canvas.addEventListener('wheel', this._mouseWheelHandler, false);
    this.canvas.addEventListener('keydown', this._keyDownHandler, false);
    this.canvas.addEventListener('touchstart', this._touchStartHandler, false);
    this.canvas.addEventListener('touchend', this._touchEndHandler, false);
    this.canvas.addEventListener('touchmove', this._touchMoveHandler, false);
    this.canvas.addEventListener('mousemove', this._mouseMoveHandler, false);
    this.canvas.addEventListener('mouseup', this._mouseUpHandler, false);
    this.canvas.addEventListener('mouseleave', this._mouseUpHandler, false);
    this.canvas.addEventListener('mouseout', this._mouseUpHandler, false);
  }

  public dispose(): void {
    this.canvas.removeEventListener('contextmenu', this._contextMenuHandler, false);
    this.canvas.removeEventListener('mousedown', this._mouseDownHandler, false);
    this.canvas.removeEventListener('wheel', this._mouseWheelHandler, false);
    this.canvas.removeEventListener('touchstart', this._touchStartHandler, false);
    this.canvas.removeEventListener('touchend', this._touchEndHandler, false);
    this.canvas.removeEventListener('touchmove', this._touchMoveHandler, false);
    this.canvas.removeEventListener('mousemove', this._mouseMoveHandler, false);
    this.canvas.removeEventListener('mouseup', this._mouseUpHandler, false);
    this.canvas.removeEventListener('mouseleave', this._mouseUpHandler, false);
    this.canvas.removeEventListener('mouseout', this._mouseUpHandler, false);
    this.canvas.removeEventListener('keydown', this._keyDownHandler, false);
  }

  public changeMode(mode: CameraMoveType): void {
    this._moveMode = mode === 'rotate' ? MOVEMODE.normal : MOVEMODE.inversed;
  }

  public getMode(): CameraMoveType {
    return this._moveMode === MOVEMODE.normal ? 'rotate' : 'move';
  }

  public setControlsState(state: STATE): void {
    this._state = state;
  }

  private onMouseDown(event: MouseEvent): void {
    if(!this.enabled || this._state !== STATE.NONE) {
      return;
    }
    event.preventDefault();
    const isRotation = event.button === this._moveMode.mouse.rotate;
    const isMove = event.button === this._moveMode.mouse.move || event.button === this._moveMode.mouse.zoom;

    if(isRotation || isMove) {
      this._state = isRotation ? STATE.ROTATE : STATE.MOVE;
    }
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.enabled || this._state === STATE.NONE) {
      return;
    }
    event.preventDefault();
    if (this._state === STATE.ROTATE) {
      this.handleMouseRotate(event);
    } else if(this._state === STATE.MOVE) {
      this.handleMouseMove(event)
    }
  }

  private onMouseUp(event: MouseEvent): void {
    if (!this.enabled || this._state === STATE.NONE) {
      return;
    }
    event.preventDefault();
    this._state = STATE.NONE;
    this.hideSceneObjects = false;
    this.update();
  }

  private handleMouseRotate(event: MouseEvent): void {
    const deltaX = event.movementX;
    const deltaY = event.movementY;

    this.handleRotate(deltaX, deltaY);
  }

  private handleMouseMove(event: MouseEvent): void {
    const deltaX = event.movementX;
    const deltaY = event.movementY;

    this.handleMove(deltaX, deltaY);
  }

  private onMouseWheel(event: WheelEvent): void {
    if (!this.enabled || this._state !== STATE.NONE) {
      return;
    }
    this.hideSceneObjects = true;

    // Get the mouse position in normalized device coordinates (-1 to +1) for both components
    const mouse = new Vector2(
      (event.offsetX / this.canvas.offsetWidth) * 2 - 1,
      -(event.offsetY / this.canvas.offsetHeight) * 2 + 1
    );

    // Remember cursor before zooming
    const originCursor = this.unprojectCursorPosition(mouse);

    this.zoomCamera(event.deltaY);
    this.camera.updateProjectionMatrix();

    // Calculate difference between cursor before and after zoom
    const zoomCursorDelta = this.unprojectCursorPosition(mouse).sub(originCursor);

    // Subtract the difference from the camera position to keep the cursor at the same position
    this.camera.position.sub(zoomCursorDelta);

    this.startWirecontourVisibilityTimeout();
    this.update();
  }

  private unprojectCursorPosition(position: Vector2): Vector3 {
    return new Vector3(position.x, position.y, (this.camera.near + this.camera.far) / (this.camera.near - this.camera.far)).unproject(this.camera);
  }

  private startWirecontourVisibilityTimeout(): void {
    this.clearWirecontourVisibilityTimeout();
    this._wirecontourVisibilityTimeout = setTimeout(() => {
      this.hideSceneObjects = false;
      this.renderScene();
    }, 250);
  }

  private clearWirecontourVisibilityTimeout(): void {
    if (this._wirecontourVisibilityTimeout) {
      clearTimeout(this._wirecontourVisibilityTimeout);
      this._wirecontourVisibilityTimeout = undefined;
    }
  }

  private onKeyPress(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }
    const isPlusButton = event.key === '+'
    const isMinusButton = event.key === '-'
    if (isPlusButton || isMinusButton) {
      event.preventDefault();
      event.stopPropagation();

      if (isPlusButton) {
        this.zoomCamera(-1);
      } else {
        this.zoomCamera(1);
      }

      this.update();
    }
  }

  private onTouchStart(touchEvent: TouchEvent): void {
    if (!this.enabled) {
      return;
    }

    switch (touchEvent.touches.length) {
      case this._moveMode.touch.rotate:
        this._state = STATE.TOUCH_ROTATE;
        break;
      case this._moveMode.touch.zoom:
        this._state = STATE.TOUCH_ZOOM;
        break;
      case this._moveMode.touch.move:
        this._state = STATE.TOUCH_MOVE;
        break;
      default:
        this._state = STATE.NONE;
        break;
    }

    if (this._state !== STATE.NONE) {
      this.hideSceneObjects = true;
    }

    this._lastTouchEvent = touchEvent;
  }

  private onTouchEnd(_event: TouchEvent): void {
    if (!this.enabled) {
      return;
    }

    this._lastTouchEvent = undefined;
    this._state = STATE.NONE;
    this.hideSceneObjects = false;
    this.update();
  }

  private onTouchMove(touchEvent: TouchEvent): void {
    if (!this.enabled) {
      return;
    }

    touchEvent.preventDefault();
    touchEvent.stopPropagation();

    switch (touchEvent.touches.length) {
      case this._moveMode.touch.rotate:
        if (this._state === STATE.TOUCH_ROTATE) {
          this.handleTouchEvent('rotate', touchEvent);
        }
        break;
      case this._moveMode.touch.zoom:
        if (this._state === STATE.TOUCH_ZOOM) {
          this.handleTouchZoom(touchEvent);
        }
        break;
      case this._moveMode.touch.move:
        if (this._state === STATE.TOUCH_MOVE) {
          this.handleTouchEvent('move', touchEvent);
        }
        break;
      default:
        this._state = STATE.NONE;
        this.hideSceneObjects = false;
    }

    this._lastTouchEvent = touchEvent;
  }

  private handleTouchEvent(eventType: 'move' | 'rotate', touchEvent: TouchEvent): void {
    if (this._lastTouchEvent === undefined) {
      return;
    }

    const deltaX = touchEvent.touches[0].pageX - this._lastTouchEvent.touches[0].pageX;
    const deltaY = touchEvent.touches[0].pageY - this._lastTouchEvent.touches[0].pageY;

    if(eventType === 'move') {
      this.handleMove(deltaX, deltaY);
    } else {
      this.handleRotate(deltaX, deltaY);
    }
  }

  private handleTouchZoom(touchEvent: TouchEvent): void {
    if (this._lastTouchEvent === undefined) {
      return;
    }

    const previousDistance = this.calculateDistance(this._lastTouchEvent.touches);
    const currentDistance = this.calculateDistance(touchEvent.touches);

    this.hideSceneObjects = true;

    const zoomDelta = new Vector2(0, currentDistance).sub(new Vector2(0, previousDistance));

    this.zoomCamera(-zoomDelta.y);

    this.update();
  }

  private calculateDistance(touches: TouchList) {
    const dx = touches[0].pageX - touches[1].pageX;
    const dy = touches[0].pageY - touches[1].pageY;

    return Math.sqrt(dx * dx + dy * dy);
  }

  private handleRotate(deltaX: number, deltaY: number): void {
    const tiltAngle = 2 * Math.PI * deltaY / this.canvas.clientHeight;
    const panAngle = 2 * Math.PI * deltaX / this.canvas.clientHeight;

    this.hideSceneObjects = true;
    this.rotateCamera(-MathUtils.toDegrees(panAngle), MathUtils.toDegrees(tiltAngle));
    this.update();
  }

  private handleMove(deltaX: number, deltaY: number) {
    const leftDistance = (deltaX * (this.camera.right - this.camera.left)) / this.camera.zoom / this.canvas.clientWidth;
    const upDistance = (deltaY * (this.camera.top - this.camera.bottom)) / this.camera.zoom / this.canvas.clientHeight;

    this.hideSceneObjects = true;
    this.moveCamera(leftDistance, upDistance);

    this.update();
  }
}
